import Typography from "@mui/material/Typography";
import {Alert, AlertTitle, Box, IconButton, useTheme} from "@mui/material";
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import NotesIcon from '@mui/icons-material/Notes';
import UndoIcon from '@mui/icons-material/Undo';
import ReceiptIcon from '@mui/icons-material/Receipt';
import {observer} from 'mobx-react';
import {useNavigate, useParams} from "react-router-dom";
import * as React from "react";
import {useState} from "react";
import OrderFeature from "./OrderFeature";
import {useApplication} from "../../ApplicationProvider";
import {useNavigatorFeature} from "../../useFeature";
import FormSection from "../../forms/FormSection";
import FormRow from "../../forms/FormRow";
import FormFileField from "../../components/fileUploadComponents/FormFileField";
import FormAutocompleteField from "../../components/fields/FormAutocompleteField";
import FormColumn from "../../forms/FormColumn";
import FormTextField from "../../components/fields/FormTextField";
import ArticleTable from "../../components/ArticleTable";
import LayoutContentToolbar from "../../components/layout/LayoutContentToolbar";
import FilledButton from "../../components/buttons/FilledButton";
import LayoutContentWrapper from "../../components/layout/LayoutContentWrapper";
import {styles} from "./create_order/CreateOrderStyles";
import FormDateField from "../../components/fields/FormDateField";
import AlertDialog from "../../components/AlertDialog";
import CustomerDebtAlertBanner from "./CustomerDebtAlertBanner";
import ArticleFormSection from "../../forms/ArticleFormSection";
import AlertDialogState from "../../components/AlertDialogState";
import {ORDER_PATH, ORDERS_PATH} from "../../routes";
import DeleteOrderModal from "./create_order/DeleteOrderModal";
import MoneyRenderer from "../../utils/MoneyRenderer";

function OrderScreen() {
    const application = useApplication();
    const theme = useTheme();
    const navigator = useNavigate();
    const {id} = useParams();
    const feature = useNavigatorFeature(() => new OrderFeature(application, navigator, id), navigator, [id])
    const style = styles(theme);
    const [isEditingFreeOrderComment, setIsEditingFreeOrderComment] = useState(false);

    const isFeatureReady = (feature) => {
        return feature && feature.loaded
    }

    const customerForm = (canEditCustomer) => {
        return (
            <FormSection title="Cliente">
                <FormRow>
                    <FormAutocompleteField label={'Razón social'} field={feature.form.getFieldByName("businessName")}
                                           options={feature.businessNameOptions()} readOnly={!canEditCustomer}/>
                    <FormAutocompleteField label={'Número de cliente'}
                                           field={feature.form.getFieldByName("customerCode")}
                                           options={feature.customerCodeOptions()} readOnly={!canEditCustomer}/>
                    <FormAutocompleteField label={'Nombre de fantasía'}
                                           field={feature.form.getFieldByName("fantasyName")}
                                           options={feature.fantasyNameOptions()} readOnly={!canEditCustomer}
                    />
                </FormRow>
            </FormSection>
        )
    }

    const isDateDisabledForDelivery = (aDateTime) => {
        return !feature.isDateValidForDelivery(aDateTime)
    }

    const deliverySiteForm = () => {
        return (
            <FormSection title="Punto de Entrega">
                <FormColumn>
                    <FormRow>
                        <FormAutocompleteField label={'Punto de entrega'}
                                               field={feature.form.getFieldByName("deliverySiteName")}
                                               options={feature.deliverySiteNameOptions()}
                                               readOnly={!feature.canEditDeliverySite()}
                                               clear={() => feature.clearDeliverySiteDateAndPurchaseOrderNumber()}/>
                        <FormAutocompleteField label={'Dirección'}
                                               field={feature.form.getFieldByName("deliverySiteAddress")}
                                               options={feature.deliverySiteAddressOptions()}
                                               readOnly={!feature.canEditDeliverySite()}
                                               clear={() => feature.clearDeliverySiteDateAndPurchaseOrderNumber()}/>
                        <FormAutocompleteField label={'Localidad'}
                                               field={feature.form.getFieldByName("deliverySiteCity")}
                                               options={feature.deliverySiteCityOptions()}
                                               readOnly={!feature.canEditDeliverySite()}/>
                    </FormRow>
                </FormColumn>
            </FormSection>
        )
    }

    const articlesForm = () => {
        return (
            <ArticleFormSection title={feature.hasArticlePriceList() ? "Artículos" : null} feature={feature}>
                {feature.hasArticlePriceList() ? <ArticleTable label={'Artículos'} form={feature.articlesForm} feature={feature}/> :
                    <div style={{
                        display: 'flex',
                        justifyContent: 'center',
                        fontFamily: 'Roboto, sans-serif',
                        marginBottom: '50px',
                        fontWeight: 400
                    }}>
                        <div style={{ maxWidth: '90%', textAlign: 'center' }}>
                            {'El cliente asociado a esta orden no tiene asignada una lista de precios'}
                        </div>
                    </div>}

                    </ArticleFormSection>
        )
    }

    const purchaseOrderNumberForm = () => {
        return (
            <FormSection title={'Número de orden de compra'}>
                <FormColumn>
                    <FormRow>
                        <FormTextField label={'Número de orden de compra'}
                               field={feature.form.getFieldByName("purchaseOrderNumber")}
                               disabled={!feature.canEditDeliverySite()}
                               onBlur={() => feature.getEnabledDatesForDeliverySiteWithPurchaseOrderNumber()}/>

                    </FormRow>
                </FormColumn>
            </FormSection>
        )
    }

    const freeOrderCommentForm = () => {
        return (
            <FormSection title="Comentario">
                {isEditingFreeOrderComment ? (
                    <Box sx={style.freeOrderCommentContainer}>
                        <FormRow>
                            <FormTextField label={'Comentario'}
                                           field={feature.freeOrderCommentField()}
                                           placeholder={'Escriba su comentario aquí...'}
                                           rows={4}
                                           onBlur={async () => {
                                               await feature.editOrdersFreeOrderComment()
                                               setIsEditingFreeOrderComment(false)
                                           }}/>
                        </FormRow>
                    </Box>
                ) : (
                    <div style={{display: 'flex', alignItems: 'center', gap: '0.5em'}}>
                        <Typography>{feature.order.observations}</Typography>
                        {feature.canEditFreeOrderComment() && (
                            <IconButton onClick={() => setIsEditingFreeOrderComment(true)}>
                                <EditIcon/>
                            </IconButton>
                        )}
                    </div>
                )}
            </FormSection>
        )
    }

    const deliveryDateForm = () => {
        return (
            <FormSection title={"Fecha de entrega"}>
                <FormColumn>
                    <FormRow>
                        <FormDateField label={'Fecha de entrega'} field={feature.form.getFieldByName("deliveryDate")}
                                       disabled={!feature.canEditDeliveryDate()}
                                       isDateDisabled={(date) => isDateDisabledForDelivery(date)}/>
                    </FormRow>
                </FormColumn>
            </FormSection>
        )
    }

    const createOrderContent = () => {
        const title = feature.isFreeOrder ? `Pedido sin cargo ${feature.orderNumber()}` : `Pedido ${feature.orderNumber()}`;
        return isFeatureReady(feature) && (
            <div>
                {feature.orderHasBeenCreated() && <div style={{alignItems: 'right', display: 'flex', justifyContent: 'right', gap: '0.5em'}}>
                    {feature.advancePaymentProofUrl() && <FilledButton
                        onClick={() => window.open(feature.advancePaymentProofUrl(), "_blank", "noopener,noreferrer")}
                    >
                        <ReceiptIcon fontSize="medium" />
                        Comprobante de pago
                    </FilledButton>}
                    <FilledButton onClick={() => navigator(ORDER_PATH + "/log/" + `${feature.orderNumber()}`) }>
                        <NotesIcon fontSize="medium"/>
                        Historial
                    </FilledButton>
                </div>}
                <LayoutContentToolbar
                    title={title}
                    breadcrumbs={[
                        {label: 'Pedidos', path: ORDERS_PATH},
                        {label: title},
                    ]}
                >
                    <div style={style.alertDialogsContainer}>
                        {feature.canReturnToInitialState() &&
                            <AlertDialog title="¿Desea volver la orden a estado abierto?"
                                         color="error" icon={<UndoIcon/>}
                                         acceptHandler={() => feature.returnOrderToInitial()}
                                         onOpen={() => feature.setActiveDialog('returnToInitialState')}
                                         onClose={() => feature.closeDialog()}
                                         dialogState={new AlertDialogState()}>
                            </AlertDialog>}
                            {feature.canDeleteOrder() && (
                                <IconButton
                                    color="error"
                                    onClick={() => feature.setIsDeletingOrder(true)}
                                >
                                    <DeleteIcon />
                                </IconButton>
                            )}
                        <DeleteOrderModal
                            feature={feature}
                        />
                    </div>
                </LayoutContentToolbar>
                {feature.shouldShowAuthorizationObservations() && (
                    <Alert severity="warning" sx={{marginBottom: '1.5rem'}}>
                        <AlertTitle>Cambios requeridos</AlertTitle>
                        {feature.authorizationObservations()}
                    </Alert>
                )}

                {feature.customerDebtAlert && <CustomerDebtAlertBanner debtAlert={feature.customerDebtAlert}/>}
                {customerForm(canEditCustomer)}
                {deliverySiteForm()}
                {purchaseOrderNumberForm()}
                {deliveryDateForm()}

                <div>
                    <Typography variant="h5" fontWeight={'800'}>
                        {articlesForm()}
                    </Typography>
                </div>
                {feature.isFreeOrder && freeOrderCommentForm()}
                {feature.orderPrices && (
                    <FormSection title="Totales">
                        <div className="totales-container">
                            <table className="table-totales" style={style.totalesTable}>
                                <tbody>
                                <tr>
                                    <td style={style.totalesCell}>SUBTOTAL</td>
                                    <td className="text-right" style={{...style.totalesValueCell, ...style.totalesRegularRowCell}}>{MoneyRenderer(feature.subtotal())}</td>
                                </tr>
                                <tr>
                                    <td style={{...style.totalesCell, ...style.totalesRegularRowCell}}>IVA</td>
                                    <td className="text-right" style={{...style.totalesValueCell, ...style.totalesRegularRowCell}}>{MoneyRenderer(feature.totalTaxes())}</td>
                                </tr>
                                <tr>
                                    <td style={{...style.totalesCell, ...style.totalesRegularRowCell}}>IIBB</td>
                                    <td className="text-right" style={{...style.totalesValueCell, ...style.totalesRegularRowCell}}>{MoneyRenderer(feature.totalGrossIncomeTaxes())}</td>
                                </tr>
                                <tr className="total-row">
                                    <td style={style.totalesRowCell}>TOTAL</td>
                                    <td className="text-right" style={style.totalesRowValueCell}>{MoneyRenderer(feature.total())}</td>
                                </tr>
                                </tbody>
                            </table>
                        </div>
                        <Typography variant="caption" sx={{
                            fontSize: '0.75rem', 
                            color: theme.palette.text.secondary, 
                            marginTop: '8px', 
                            fontStyle: 'italic'
                        }}>
                            * Los precios mostrados son solo una referencia y no representan los valores finales.
                        </Typography>
                    </FormSection>
                )}
                {feature.advancePaymentRequired() && (
                    <FormSection title="Pago Anticipado">
                        <FormFileField
                            title="Comprobante de pago"
                            placeholder="Debe ingresar comprobante de pago para poder pasar el pedido a cerrado"
                            singleFile={true}
                            isRequired={true}
                            form={feature.advancePaymentProofForm()}
                            fieldName="advancePaymentProof"
                            onBlur={() => feature.uploadAdvancePaymentProofFromForm()}
                            readOnly={!feature.orderIsOnInitialState()}
                        />
                    </FormSection>
                )}
                    <div style={style.buttonsContainer}>
                        <FilledButton
                            disabled={!feature.canMarkOrderAsPending()}
                            onClick={() => feature.markOrderAsPending()}
                        >
                            Pasar a Cerrado
                        </FilledButton>
                    </div>
            </div>
        )
    }

    const canEditCustomer = isFeatureReady(feature) && feature.canEditCustomer()
    return (
        <LayoutContentWrapper isLoading={!isFeatureReady(feature)} renderContent={() => createOrderContent()}/>
    );
}

export default observer(OrderScreen);
